/**
 * Occasion Detail Modal
 * 
 * Shows all related data for an occasion:
 * - Grocery lists (with link to view/create)
 * - Scheduled meals (with link to schedule)
 * - Menu plans (with link to view/create)
 * - Actions to create any missing items
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/authContext';
import { getOccasionRelatedData, bulkScheduleOccasion } from '../../services/unifiedPlanningService';
import { generateGroceryListFromOccasion } from '../../services/api';
import menuPlanningService from '../../services/menuPlanningService';
import SignedImage from '../SignedImage';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral50: '#F9F8F6'
};

const backgroundImages = {
    tomato: '/images/menuScheduling/tomato_bg.png',
    lemon: '/images/menuScheduling/lemon_bg.png',
    spinach: '/images/menuScheduling/spinach_bg.png'
};

const OccasionDetailModal = ({ occasionId, onClose, onUpdate }) => {
    const { user, userProfile } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    // Get planning theme
    const planningTheme = userProfile?.session_preferences?.planning_theme || 
                        localStorage.getItem('planning_theme') || 
                        'tomato';

    const loadData = useCallback(async () => {
        if (!occasionId || !user) return;
        
        try {
            setLoading(true);
            setError(null);
            const token = await user.getIdToken();
            const result = await getOccasionRelatedData(occasionId, token);
            console.log('[OccasionDetailModal] Loaded data:', {
                occasionId,
                menuPlansCount: result.menuPlans?.length || 0,
                groceryListsCount: result.groceryLists?.length || 0,
                scheduledMealsCount: result.scheduledMeals?.length || 0
            });
            setData(result);
        } catch (err) {
            console.error('Error loading occasion data:', err);
            setError(err.message || 'Failed to load occasion data');
        } finally {
            setLoading(false);
        }
    }, [occasionId, user]);

    useEffect(() => {
        if (occasionId && user) {
            console.log('[OccasionDetailModal] Loading data for occasion:', occasionId);
            // Always reload when occasionId changes or modal is opened
            loadData();
        }
    }, [occasionId, user, loadData]);
    
    // Force reload when modal becomes visible (user switches back to tab)
    useEffect(() => {
        if (!occasionId || !user) return;
        
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('[OccasionDetailModal] Tab became visible, forcing data reload...');
                loadData();
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [occasionId, user, loadData]);
    
    // Refresh data when modal becomes visible (user returns to tab or reopens modal)
    useEffect(() => {
        if (!occasionId || !user) return;
        
        const handleFocus = () => {
            console.log('[OccasionDetailModal] Window focused, refreshing data...');
            loadData();
        };
        
        window.addEventListener('focus', handleFocus);
        
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [occasionId, user, loadData]);

    // Listen for grocery list and menu plan updates from other pages
    useEffect(() => {
        if (!occasionId || !user) return;
        
        const handleGroceryListUpdate = (event) => {
            // Check if the created list is linked to this occasion
            const { listId, occasionId: createdOccasionId } = event.detail || {};
            if (createdOccasionId === occasionId || !createdOccasionId) {
                // Reload data if the list is for this occasion, or if we don't know
                // (in which case we reload to be safe)
                console.log('[OccasionDetailModal] Grocery list created, refreshing...');
                loadData();
            }
        };

        const handleMenuPlanUpdate = (event) => {
            // Check if the created plan is linked to this occasion
            const { planId, occasionId: createdOccasionId } = event.detail || {};
            console.log('[OccasionDetailModal] Menu plan event received:', { planId, createdOccasionId, currentOccasionId: occasionId });
            if (createdOccasionId === occasionId || !createdOccasionId) {
                console.log('[OccasionDetailModal] Menu plan created/updated, refreshing...');
                loadData();
            }
        };

        window.addEventListener('groceryListCreated', handleGroceryListUpdate);
        window.addEventListener('menuPlanCreated', handleMenuPlanUpdate);
        window.addEventListener('menuPlanUpdated', handleMenuPlanUpdate);

        return () => {
            window.removeEventListener('groceryListCreated', handleGroceryListUpdate);
            window.removeEventListener('menuPlanCreated', handleMenuPlanUpdate);
            window.removeEventListener('menuPlanUpdated', handleMenuPlanUpdate);
        };
    }, [occasionId, user, loadData]);

    const handleGenerateGroceryList = async () => {
        try {
            setActionLoading('grocery');
            setError(null);
            const token = await user.getIdToken();
            const result = await generateGroceryListFromOccasion(occasionId, token);
            
            // Reload data to show the newly created grocery list
            await loadData();
            if (onUpdate) onUpdate();
            
            // Navigate to grocery list page
            navigate(`/grocery-list?listId=${result.id}`);
            onClose();
        } catch (err) {
            console.error('Error generating grocery list:', err);
            setError(err.message || 'Failed to generate grocery list');
        } finally {
            setActionLoading(null);
        }
    };

    const handleScheduleOccasion = async () => {
        const startDate = prompt('Enter start date (YYYY-MM-DD):');
        if (!startDate) return;

        try {
            setActionLoading('schedule');
            setError(null);
            const token = await user.getIdToken();
            await bulkScheduleOccasion(occasionId, startDate, ['breakfast', 'lunch', 'dinner'], token);
            
            // Reload data and refresh parent
            await loadData();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('Error scheduling occasion:', err);
            setError(err.message || 'Failed to schedule occasion');
        } finally {
            setActionLoading(null);
        }
    };

    const handleCreateMenuPlan = async () => {
        try {
            setActionLoading('menuPlan');
            setError(null);
            const token = await user.getIdToken();
            
            // Get occasion recipes
            if (!data?.occasion?.recipes || data.occasion.recipes.length === 0) {
                setError('Occasion has no recipes');
                return;
            }

            const recipeIds = data.occasion.recipes.map(r => r.recipe_id);
            
            // Generate schedule
            const scheduleResult = await menuPlanningService.scheduleRecipes(recipeIds, { priority: 'recipeOrder' }, token);
            
            // Save menu plan
            const planName = data.occasion.name || 'Menu Plan';
            const saveResult = await menuPlanningService.saveMenuPlan(
                planName,
                recipeIds,
                scheduleResult,
                token,
                occasionId
            );
            
            // Dispatch event to notify other components
            window.dispatchEvent(new CustomEvent('menuPlanCreated', {
                detail: { 
                    planId: saveResult.id,
                    occasionId: occasionId,
                    planName: planName
                }
            }));
            
            // Reload data to show the newly created menu plan
            await loadData();
            if (onUpdate) onUpdate();
            
            // Small delay to ensure data is refreshed before navigation
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Navigate to schedule page
            navigate('/schedule', {
                state: {
                    schedule: {
                        recipes: data.occasion.recipes.map(r => ({ id: r.recipe_id, name: r.recipe_name })),
                        segments: scheduleResult.segments,
                        makespanSec: scheduleResult.makespanSec,
                        activeTotalSec: scheduleResult.activeTotalSec,
                        inactiveTotalSec: scheduleResult.inactiveTotalSec
                    },
                    occasionId: occasionId,
                    menuPlanId: saveResult.id
                }
            });
            onClose();
        } catch (err) {
            console.error('Error creating menu plan:', err);
            setError(err.message || 'Failed to create menu plan');
        } finally {
            setActionLoading(null);
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    // Get week view for scheduled meals
    const getWeekView = () => {
        if (!data?.scheduledMeals || data.scheduledMeals.length === 0) return null;

        // Group meals by date
        const mealsByDate = {};
        data.scheduledMeals.forEach(meal => {
            const dateKey = new Date(meal.scheduledDate).toISOString().split('T')[0];
            if (!mealsByDate[dateKey]) mealsByDate[dateKey] = [];
            mealsByDate[dateKey].push(meal);
        });

        // Get the week range
        const dates = Object.keys(mealsByDate).sort();
        if (dates.length === 0) return null;

        const firstDate = new Date(dates[0]);
        const lastDate = new Date(dates[dates.length - 1]);
        
        // Get start of week (Sunday)
        const startOfWeek = new Date(firstDate);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        
        // Get end of week (Saturday)
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 6);

        // Generate week days
        const weekDays = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(startOfWeek);
            day.setDate(day.getDate() + i);
            const dateKey = day.toISOString().split('T')[0];
            weekDays.push({
                date: day,
                dateKey,
                meals: mealsByDate[dateKey] || []
            });
        }

        return weekDays;
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-8 hairline max-w-md w-full mx-4">
                    <p className="font-mono text-sm uppercase tracking-wider text-center">
                        Loading...
                    </p>
                </div>
            </div>
        );
    }

    if (!data) {
        return null;
    }

    const { occasion, groceryLists, scheduledMeals, menuPlans } = data;
    const weekView = getWeekView();

    return (
        <div 
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{
                backgroundImage: `url(${backgroundImages[planningTheme]})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.3)'
            }}
            onClick={onClose}
        >
            <div 
                className="bg-white hairline max-w-5xl w-full max-h-[90vh] overflow-y-auto relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Floating Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-2xl border-none bg-transparent p-0 m-0 w-8 h-8 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                >
                    ×
                </button>

                {/* Error Message */}
                {error && (
                    <div className="p-4 bg-red-50 hairline text-red-900 font-mono text-sm">
                        {error}
                        <button onClick={() => setError(null)} className="float-right font-mono">×</button>
                    </div>
                )}

                {/* Content */}
                <div className="p-8 pt-12">
                    {/* Title */}
                    <div className="mb-6">
                        <h2 className="font-sans text-3xl font-medium mb-2" style={{ color: colorTokens.oxfordBlue }}>
                            {occasion.name}
                        </h2>
                        {occasion.description && (
                            <p className="font-sans text-sm text-neutral-900 opacity-60 mb-2">
                                {occasion.description}
                            </p>
                        )}
                        <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                            {occasion.recipe_count || 0} {occasion.recipe_count === 1 ? 'recipe' : 'recipes'}
                        </p>
                    </div>

                    {/* Two Column Layout: Grocery Lists and Menu Plans */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Grocery Lists Section */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-sans text-xl font-medium" style={{ color: colorTokens.oxfordBlue }}>
                                    Grocery Lists
                                </h3>
                                {groceryLists.length === 0 && (
                                    <button
                                        onClick={handleGenerateGroceryList}
                                        disabled={actionLoading === 'grocery'}
                                        className="px-3 py-1.5 font-mono text-xs uppercase tracking-wider"
                                        style={{ 
                                            backgroundColor: colorTokens.oxfordBlue, 
                                            color: '#FFFFFF',
                                            opacity: actionLoading === 'grocery' ? 0.5 : 1
                                        }}
                                    >
                                        {actionLoading === 'grocery' ? 'Generating...' : 'Generate'}
                                    </button>
                                )}
                            </div>

                            {groceryLists.length === 0 ? (
                                <div className="p-4 bg-neutral-50 hairline">
                                    <p className="font-mono text-sm text-neutral-900 opacity-60">
                                        No grocery list created yet
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {groceryLists.map(list => (
                                        <div
                                            key={list.id}
                                            className="p-4 bg-neutral-50 hairline cursor-pointer hover:bg-neutral-900 hover:text-white transition-all"
                                            onClick={() => navigate(`/grocery-list?listId=${list.id}`)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-sans text-base font-medium">
                                                        {list.name}
                                                    </p>
                                                    <p className="font-mono text-xs uppercase tracking-wider opacity-60 mt-1">
                                                        {list.itemCount} items • {formatDate(list.createdAt)}
                                                    </p>
                                                </div>
                                                {list.isLocked && (
                                                    <span className="font-mono text-xs">🔒</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Menu Plans Section */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-sans text-xl font-medium" style={{ color: colorTokens.oxfordBlue }}>
                                    Menu Plans
                                </h3>
                                {menuPlans.length === 0 && (
                                    <button
                                        onClick={handleCreateMenuPlan}
                                        disabled={actionLoading === 'menuPlan'}
                                        className="px-3 py-1.5 font-mono text-xs uppercase tracking-wider"
                                        style={{ 
                                            backgroundColor: colorTokens.oxfordBlue, 
                                            color: '#FFFFFF',
                                            opacity: actionLoading === 'menuPlan' ? 0.5 : 1
                                        }}
                                    >
                                        {actionLoading === 'menuPlan' ? 'Creating...' : 'Create'}
                                    </button>
                                )}
                            </div>

                            {menuPlans.length === 0 ? (
                                <div className="p-4 bg-neutral-50 hairline">
                                    <p className="font-mono text-sm text-neutral-900 opacity-60">
                                        No menu plan created yet
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {menuPlans.map(plan => (
                                        <div
                                            key={plan.id}
                                            className="p-4 bg-neutral-50 hairline cursor-pointer hover:bg-neutral-900 hover:text-white transition-all"
                                            onClick={async () => {
                                                try {
                                                    setActionLoading('loadingPlan');
                                                    // Fetch the full menu plan data
                                                    const token = await user.getIdToken();
                                                    const fullPlan: any = await menuPlanningService.getMenuPlan(plan.id, token);
                                                    
                                                    // Navigate to schedule page with the menu plan data
                                                    navigate('/schedule', {
                                                        state: {
                                                            schedule: {
                                                                recipes: (fullPlan.recipe_ids || []).map((id: number, idx: number) => ({
                                                                    id,
                                                                    name: (fullPlan.recipe_names || [])[idx] || `Recipe ${id}`
                                                                })),
                                                                segments: fullPlan.schedule_data?.segments || [],
                                                                makespanSec: fullPlan.makespan_sec || fullPlan.makespanSec || 0,
                                                                activeTotalSec: fullPlan.schedule_data?.active_total_sec || 0,
                                                                inactiveTotalSec: fullPlan.schedule_data?.inactive_total_sec || 0
                                                            },
                                                            occasionId: occasionId,
                                                            menuPlanId: plan.id
                                                        }
                                                    });
                                                } catch (err) {
                                                    console.error('Error loading menu plan:', err);
                                                    setError(err.message || 'Failed to load menu plan');
                                                } finally {
                                                    setActionLoading(null);
                                                }
                                            }}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-sans text-base font-medium">
                                                        {plan.name}
                                                    </p>
                                                    <p className="font-mono text-xs uppercase tracking-wider opacity-60 mt-1">
                                                        {plan.recipeCount} recipes • {formatDate(plan.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Scheduled Meals Section */}
                    <div className="hairline p-6 bg-white">
                        <div className="flex items-center justify-between mb-4">
                            <h3 
                                className="font-sans text-xl font-medium"
                                style={{ color: colorTokens.oxfordBlue }}
                            >
                                Scheduled Meals
                            </h3>
                                {scheduledMeals.length === 0 && (
                                    <button
                                        onClick={handleScheduleOccasion}
                                        disabled={actionLoading === 'schedule'}
                                        className="px-3 py-1.5 font-mono text-xs uppercase tracking-wider"
                                        style={{ 
                                            backgroundColor: colorTokens.oxfordBlue, 
                                            color: '#FFFFFF',
                                            opacity: actionLoading === 'schedule' ? 0.5 : 1
                                        }}
                                    >
                                        {actionLoading === 'schedule' ? 'Scheduling...' : 'Schedule'}
                                    </button>
                                )}
                        </div>

                        {scheduledMeals.length === 0 ? (
                            <div className="p-4 bg-neutral-50 hairline">
                                <p className="font-mono text-sm text-neutral-900 opacity-60 text-center">
                                    No meals scheduled yet
                                </p>
                            </div>
                        ) : weekView ? (
                            <div className="bg-neutral-50 hairline p-4">
                                <div className="grid grid-cols-7 gap-2">
                                    {weekView.map((day, idx) => (
                                        <div key={idx} className="text-center">
                                            <p className="font-mono text-xs uppercase tracking-wider mb-2 text-neutral-900 opacity-60">
                                                {day.date.toLocaleDateString('en-US', { weekday: 'short' })}
                                            </p>
                                            <p className="font-mono text-[10px] uppercase tracking-wider mb-2 text-neutral-900 opacity-60">
                                                {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            </p>
                                            <div className="space-y-1 min-h-[60px]">
                                                {day.meals.map((meal, mealIdx) => (
                                                    <div
                                                        key={mealIdx}
                                                        className="p-1 bg-white hairline cursor-pointer hover:bg-neutral-900 hover:text-white transition-all"
                                                        onClick={() => navigate('/menu-scheduling')}
                                                    >
                                                        <p className="font-sans text-[10px] font-medium truncate">
                                                            {meal.recipeName}
                                                        </p>
                                                        {meal.mealType && (
                                                            <p className="font-mono text-[8px] uppercase tracking-wider opacity-60">
                                                                {meal.mealType}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 bg-neutral-50 hairline">
                                <p className="font-mono text-sm text-neutral-900 opacity-60">
                                    No meals scheduled yet
                                </p>
                            </div>
                        )}

                        {/* Recipe Cards */}
                        {occasion.recipes && occasion.recipes.length > 0 && (
                            <div className="mt-6">
                                <h4 
                                    className="font-sans text-lg font-medium mb-4"
                                    style={{ color: colorTokens.oxfordBlue }}
                                >
                                    Recipes
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {occasion.recipes.map((recipe) => {
                                        // Get image - try recipe_images first (from recipes table), then fallback to images
                                        let recipeImage = null;
                                        if (recipe.recipe_images) {
                                            // recipe_images is a JSONB array from the recipes table
                                            const images = typeof recipe.recipe_images === 'string' 
                                                ? JSON.parse(recipe.recipe_images) 
                                                : recipe.recipe_images;
                                            recipeImage = Array.isArray(images) && images.length > 0 ? images[0] : null;
                                        } else if (recipe.images) {
                                            const images = typeof recipe.images === 'string' 
                                                ? JSON.parse(recipe.images) 
                                                : recipe.images;
                                            recipeImage = Array.isArray(images) && images.length > 0 ? images[0] : null;
                                        }

                                        return (
                                            <div
                                                key={recipe.recipe_id || recipe.id}
                                                className="bg-neutral-50 hairline cursor-pointer hover:bg-neutral-900 hover:text-white transition-all group"
                                                onClick={() => navigate(`/recipe/${recipe.recipe_id || recipe.id}`)}
                                            >
                                                <div className="aspect-square w-full overflow-hidden bg-neutral-100">
                                                    <SignedImage
                                                        src={recipeImage}
                                                        alt={recipe.recipe_name || recipe.name}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                        category={recipe.recipe_category || recipe.category}
                                                    />
                                                </div>
                                                <div className="p-3">
                                                    <h5 className="font-sans text-sm font-medium line-clamp-2 mb-1">
                                                        {recipe.recipe_name || recipe.name}
                                                    </h5>
                                                    {recipe.servings && (
                                                        <p className="font-mono text-xs uppercase tracking-wider opacity-60">
                                                            {recipe.servings} servings
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OccasionDetailModal;
